/**
 * useRecaptcha — Hook para obtener token reCAPTCHA Enterprise score-based.
 *
 * SEC-B2 (mayo 2026): además de Firebase App Check que valida cada request
 * automáticamente, ciertos callables sensibles (compras, kill switches, MFA
 * disable, etc) piden un token score-based EXPLÍCITO con una `action`
 * declarada. Eso permite al backend validar:
 *   1. Score >= threshold (ej. 0.7 para compras)
 *   2. Action en el token matchea la esperada (anti-replay cross-action)
 *
 * USO
 * ───
 *   const { execute } = useRecaptcha()
 *
 *   async function handleComprar() {
 *     const token = await execute('comprar_paquete_ia')
 *     const fn = httpsCallable(functions, 'comprarPaqueteIA')
 *     await fn({ ...datos, recaptchaToken: token })
 *   }
 *
 * SETUP
 * ─────
 * Necesita VITE_RECAPTCHA_SITE_KEY definida en el .env. La key se crea en
 * GCP Console → Security → reCAPTCHA Enterprise (tipo "Score-based").
 * MISMA key que usa Firebase App Check.
 *
 * Si la env var no está seteada (dev local), `execute()` devuelve null y el
 * backend está en modo passthrough (`verifyRecaptcha.js` lo detecta y skip).
 */

import { useCallback, useEffect, useState } from 'react'
import { createLogger } from '@core/lib/logger'

const logger = createLogger('useRecaptcha')

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY

// Estado global (compartido entre todos los hooks del árbol):
//   'idle'    — todavía no chequeamos
//   'ready'   — grecaptcha.enterprise disponible Y .ready() resolvió
//   'error'   — timeout esperando a grecaptcha (probable: env var faltante,
//               CSP bloqueando, o key inválida en dominio)
let _scriptState = 'idle'
let _scriptPromise = null

// El script de reCAPTCHA Enterprise se carga vía <script> tag en index.html
// con ?render=<KEY> resuelto por Vite en build time. Esto garantiza que la
// key se registre ANTES que cualquier JS corra (incluido Firebase App Check).
// Acá solo esperamos a que `window.grecaptcha.enterprise.ready()` resuelva.
function ensureRecaptchaScript() {
  if (_scriptState === 'ready') return Promise.resolve(true)
  if (_scriptState === 'error') return Promise.resolve(false)
  if (_scriptPromise) return _scriptPromise

  _scriptPromise = new Promise((resolve) => {
    if (typeof window === 'undefined' || !RECAPTCHA_SITE_KEY) {
      _scriptState = 'error'
      resolve(false)
      return
    }

    // Poll cada 50ms hasta que grecaptcha.enterprise aparezca o timeout (10s).
    // El script tiene `async defer`, así que puede llegar después del primer
    // useEffect. No queremos appendear otro script tag — eso recargaría la
    // librería y puede causar conflictos con App Check.
    const startedAt = Date.now()
    const TIMEOUT_MS = 10_000
    const POLL_MS = 50

    const waitForGrecaptcha = () => {
      if (window.grecaptcha?.enterprise) {
        window.grecaptcha.enterprise.ready(() => {
          _scriptState = 'ready'
          resolve(true)
        })
        return
      }
      if (Date.now() - startedAt > TIMEOUT_MS) {
        _scriptState = 'error'
        logger.warn('recaptcha.script_load_timeout', { waited_ms: TIMEOUT_MS })
        resolve(false)
        return
      }
      setTimeout(waitForGrecaptcha, POLL_MS)
    }
    waitForGrecaptcha()
  })
  return _scriptPromise
}

export function useRecaptcha() {
  const [ready, setReady] = useState(_scriptState === 'ready')

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) {
      setReady(false)
      return
    }
    ensureRecaptchaScript().then((ok) => setReady(ok))
  }, [])

  const execute = useCallback(async (action) => {
    if (!RECAPTCHA_SITE_KEY) {
      // Modo passthrough en dev local. El backend lo detecta y skip.
      return null
    }
    const scriptOk = await ensureRecaptchaScript()
    if (!scriptOk) {
      logger.warn('recaptcha.script_not_available', { action })
      return null
    }
    try {
      return await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action })
    } catch (err) {
      logger.warn('recaptcha.execute_failed', { action, error: err?.message })
      return null
    }
  }, [])

  return { execute, ready }
}
